import React from 'react';
import styled from 'styled-components';
import { UnstyledLink } from '@zocdoc-frontend-common/components-link';
import {
    FacebookLoginButtonTransparent,
    GoogleLoginButtonTransparent,
    AppleLoginButtonTransparent,
} from '@zocdoc-frontend-common/components-sociallogin';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import { buildSsoLoginUrl } from '@zocdoc-frontend-common/utils-react/buildSsoLoginUrl';
import {
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc/frontend-metrics';
import { Metrics } from './constants';

/**
 *
 * @param {string} baseFacebookLoginUrl
 * @param {string} baseGoogleLoginUrl
 * @param {string} baseAppleLoginUrl
 * @param {boolean} rememberMe
 * @param {string} returnUrl
 * @param {boolean} showRememberMe
 * @param {function} trackClickFacebookLogin
 * @param {function} trackClickGoogleLogin
 * @param {function} trackClickAppleLogin
 * @param {function} sendMetric
 * @returns {JSX.Element}
 * @constructor
 */
const BaseSocialLoginButtons = ({
    baseFacebookLoginUrl,
    baseGoogleLoginUrl,
    baseAppleLoginUrl,
    rememberMe,
    returnUrl,
    showRememberMe,
    trackClickFacebookLogin,
    trackClickGoogleLogin,
    trackClickAppleLogin,
    sendMetric,
}) => {
    const facebookLoginUrl = buildSsoLoginUrl(
        baseFacebookLoginUrl,
        returnUrl,
        rememberMe,
        showRememberMe
    );
    const googleLoginUrl = buildSsoLoginUrl(
        baseGoogleLoginUrl,
        returnUrl,
        rememberMe,
        showRememberMe
    );

    const handleClickGoogleLogin = () => {
        trackClickGoogleLogin();
        sendMetric({
            action: Metrics.ClickGoogle,
            element: 'GoogleSingleSignOnButton',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    const handleClickFacebookLogin = () => {
        trackClickFacebookLogin();
        sendMetric({
            action: Metrics.ClickFacebook,
            element: 'FacebookSingleSignOnButton',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    return (
        <div>
            <SsoLoginLink
                beforeClick={handleClickGoogleLogin}
                data-test="login-form-google-login-link"
                href={googleLoginUrl}
            >
                <GoogleLoginButtonTransparent
                    message={'Continue with Google'}
                />
            </SsoLoginLink>
            <SsoLoginLink
                beforeClick={handleClickFacebookLogin}
                data-test="login-form-facebook-login-link"
                href={facebookLoginUrl}
            >
                <FacebookLoginButtonTransparent
                    message={'Continue with Facebook'}
                />
            </SsoLoginLink>
            <AppleLoginButton
                baseAppleLoginUrl={baseAppleLoginUrl}
                rememberMe={rememberMe}
                returnUrl={returnUrl}
                showRememberMe={showRememberMe}
                trackClickAppleLogin={trackClickAppleLogin}
                sendMetric={sendMetric}
            />
        </div>
    );
};

const AppleLoginButton = ({
    baseAppleLoginUrl,
    rememberMe,
    returnUrl,
    showRememberMe,
    trackClickAppleLogin,
    sendMetric,
}) => {
    const appleLoginUrl = buildSsoLoginUrl(
        baseAppleLoginUrl,
        returnUrl,
        rememberMe,
        showRememberMe
    );

    const handleClickAppleLogin = () => {
        trackClickAppleLogin();
        sendMetric({
            action: Metrics.ClickApple,
            element: 'AppleSingleSignOnButton',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
        });
    };

    if (appleLoginUrl) {
        return (
            <SsoLoginLink
                beforeClick={handleClickAppleLogin}
                data-test="login-form-apple-login-link"
                href={appleLoginUrl}
            >
                <AppleLoginButtonTransparent message={'Continue with Apple'} />
            </SsoLoginLink>
        );
    }

    return null;
};

export const SocialLoginButtons = withCustomMetrics({
    component: 'SocialLoginButtons',
})(BaseSocialLoginButtons);

const SsoLoginLink = styled(UnstyledLink)`
    width: 100%;
    display: block;
    margin-bottom: 8px;

    ${breakpoint.small} {
        margin-bottom: 20px;
    }
`;
